/**
 * Universal RUL Form Component
 *
 * The ONE component to rule them all! 🎯
 *
 * Philosophy: Backend owns EVERYTHING. Frontend just renders.
 *
 * - Auto-detects hierarchical vs flat format
 * - Renders ANY widget type from registry
 * - Works with ANY backend service URL
 * - Zero knowledge of business logic
 *
 * Usage:
 *   <UniversalRULForm formUrl="/formsexamples/rul/forms/text_input_showcase" />
 *   <UniversalRULForm formUrl="/rul/forms/login-example" />
 *   <UniversalRULForm formUrl="/custom-service/forms/my-form" />
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Row, Col, Spin, Alert, Button } from 'antd';
import { debounce } from 'lodash';
import {
  useUniversalRULForm,
  useUniversalRULFieldUpdate,
  useUniversalRULSubmit,
} from '@/hooks/useUniversalRUL';
import { renderWidget } from './widgets/registry';
import { evaluateExpression } from './utils/expressionEvaluator';
import './RULForm.css';

interface UniversalRULFormProps {
  formUrl: string;
  onSuccess?: (data: any) => void;
  onError?: (errors: Record<string, string>) => void;
  className?: string;
  debounceMs?: number;
  showSubmitButton?: boolean; // If false, form doesn't show submit button (backend controls it)
}

export const UniversalRULForm: React.FC<UniversalRULFormProps> = ({
  formUrl,
  onSuccess,
  onError,
  className = '',
  debounceMs = 300,
  showSubmitButton = true,
}) => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [warnings, setWarnings] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [localFormData, setLocalFormData] = useState<any>(null);

  // Ref for error alert to scroll into view
  const errorAlertRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to error when submitError changes
  useEffect(() => {
    if (submitError && errorAlertRef.current) {
      errorAlertRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [submitError]);

  // Track ALL field values for PATCH requests (backend is stateless)
  // We send all values, not just __state_fields, to ensure form state is fully preserved
  const allFieldValuesRef = useRef<Record<string, any>>({});

  // Store __form_meta from GET response (contains patch_url, submit_url patterns, and default_patch_trigger)
  const formMetaRef = useRef<{
    patch_url?: string;
    submit_url?: string;
    submit_method?: string; // POST for create, PUT for edit
    submit_label?: string; // e.g., "Create Flight" or "Update Flight"
    mode?: 'create' | 'edit';
    default_patch_trigger?: 'blur' | 'change' | 'keyup';
  } | null>(null);

  // Store __state_fields - fields that MUST be included in form_state for PATCH requests
  const stateFieldsRef = useRef<string[]>([]);

  // Store __prefilled - fields that were pre-populated by backend
  const prefilledFieldsRef = useRef<string[]>([]);

  // Helper: Get patch trigger for a field (field.patch_trigger || default || 'blur')
  const getPatchTrigger = useCallback(
    (widget: any): 'blur' | 'change' | 'keyup' => {
      if (widget?.patch_trigger) return widget.patch_trigger;
      return formMetaRef.current?.default_patch_trigger || 'blur';
    },
    []
  );

  // Helper: Frontend validation before PATCH
  const validateFrontend = useCallback((widget: any, value: any): boolean => {
    if (!widget?.frontend_validation) return true;

    const { pattern, pattern_message } = widget.frontend_validation;

    if (pattern && value) {
      try {
        const regex = new RegExp(pattern);
        if (!regex.test(String(value))) {
          // Set frontend validation error
          setErrors((prev) => ({
            ...prev,
            [widget.name]: pattern_message || 'Invalid format',
          }));
          return false;
        }
      } catch {
        // Invalid regex pattern, skip validation
        console.warn(
          `Invalid regex pattern for field ${widget.name}:`,
          pattern
        );
      }
    }

    // Clear frontend validation error if valid
    setErrors((prev) => {
      const next = { ...prev };
      delete next[widget.name];
      return next;
    });
    return true;
  }, []);

  // Load form from ANY URL
  const {
    data: formData,
    isLoading,
    error: loadError,
  } = useUniversalRULForm(formUrl);

  // Field update mutation
  const updateFieldMutation = useUniversalRULFieldUpdate(formUrl);

  // Form submission mutation
  const submitMutation = useUniversalRULSubmit(formUrl);

  // Helper: Extract field value from widget (handles dropdown selected_option)
  const getWidgetValue = (widget: any): any => {
    // If value is explicitly set, use it
    if (widget.value !== null && widget.value !== undefined) {
      return widget.value;
    }
    // For dropdowns, value might be in selected_option.id
    if (widget.selected_option?.id !== undefined) {
      console.log(
        '[RUL] getWidgetValue using selected_option.id for',
        widget.name,
        ':',
        widget.selected_option.id
      );
      return widget.selected_option.id;
    }
    return null;
  };

  // Helper: Extract and update ALL field values from form response
  const updateStateFromFormData = useCallback((data: any) => {
    if (!data) return;

    // Extract all field values from the form
    if (data.__root) {
      // Flat format
      for (const fieldName of data.__root) {
        const widget = data[fieldName];
        if (widget && typeof widget === 'object') {
          const value = getWidgetValue(widget);
          allFieldValuesRef.current[fieldName] = value;
        }
      }
    } else if (data.rows) {
      // Hierarchical format
      data.rows.forEach((row: any) => {
        row.columns.forEach((col: any) => {
          col.widgets.forEach((widget: any) => {
            if (widget.name) {
              const value = getWidgetValue(widget);
              allFieldValuesRef.current[widget.name] = value;
            }
          });
        });
      });
    }

    // IMPORTANT: Also capture values from __prefilled and __state_fields
    // These fields may be on root level but NOT in __root array or rows
    // Use refs as fallback since PATCH responses may not include these arrays
    const prefilledFields =
      data.__prefilled || prefilledFieldsRef.current || [];
    const stateFields = data.__state_fields || stateFieldsRef.current || [];
    const allSpecialFields = [...new Set([...prefilledFields, ...stateFields])];

    for (const fieldName of allSpecialFields) {
      const widget = data[fieldName];
      if (widget && typeof widget === 'object') {
        const value = getWidgetValue(widget);
        // Only set if we got a value (don't overwrite with null if field was already captured)
        if (value !== null || !(fieldName in allFieldValuesRef.current)) {
          allFieldValuesRef.current[fieldName] = value;
        }
        console.log(
          '[RUL] Captured prefilled/state field:',
          fieldName,
          '=',
          value
        );
      }
    }

    // Debug: Log captured state fields
    if (stateFieldsRef.current.length > 0) {
      console.log('[RUL] State fields:', stateFieldsRef.current);
      console.log(
        '[RUL] Current form_state values:',
        Object.fromEntries(
          stateFieldsRef.current.map((f) => [f, allFieldValuesRef.current[f]])
        )
      );
    }
  }, []);

  // Update local form data when server data changes
  useEffect(() => {
    if (formData) {
      setLocalFormData(formData);
      // Store __form_meta for URL patterns
      if (formData.__form_meta) {
        formMetaRef.current = formData.__form_meta;
        console.log('[RUL] Form meta loaded:', formData.__form_meta);
      }
      // Store __state_fields - fields that MUST be in form_state for PATCH
      if (formData.__state_fields) {
        stateFieldsRef.current = formData.__state_fields;
        console.log('[RUL] State fields loaded:', formData.__state_fields);
      }
      // Store __prefilled - fields that were pre-populated by backend
      if (formData.__prefilled) {
        prefilledFieldsRef.current = formData.__prefilled;
        console.log('[RUL] Prefilled fields loaded:', formData.__prefilled);
      }
      // Update state values (must be after setting refs)
      updateStateFromFormData(formData);
    }
  }, [formData, updateStateFromFormData]);

  // Auto-detect format
  const isHierarchical = localFormData?.rows;
  const isFlat = localFormData?.__root;

  // Helper: Merge PATCH response with existing form data
  // Backend now returns only changed widgets, not the full form
  const mergeFormResponse = useCallback(
    (response: any) => {
      if (!localFormData) return;

      setLocalFormData((prev: any) => {
        if (!prev) return prev;

        const updated = JSON.parse(JSON.stringify(prev)); // Deep clone

        // Merge widgets from response (skip __ prefixed keys)
        for (const [key, widget] of Object.entries(response)) {
          if (!key.startsWith('__') && widget && typeof widget === 'object') {
            if (updated.__root) {
              // Flat format - update widget directly
              updated[key] = widget;
            } else if (updated.rows) {
              // Hierarchical format - find and update widget in rows
              for (const row of updated.rows) {
                for (const col of row.columns) {
                  const widgetIndex = col.widgets.findIndex(
                    (w: any) => w.name === key
                  );
                  if (widgetIndex !== -1) {
                    col.widgets[widgetIndex] = widget;
                    break;
                  }
                }
              }
            }
          }
        }

        // Handle __resets - set widget.value to null
        if (response.__resets) {
          response.__resets.forEach((fieldName: string) => {
            if (updated.__root && updated[fieldName]) {
              updated[fieldName] = { ...updated[fieldName], value: null };
            } else if (updated.rows) {
              for (const row of updated.rows) {
                for (const col of row.columns) {
                  const widget = col.widgets.find(
                    (w: any) => w.name === fieldName
                  );
                  if (widget) {
                    widget.value = null;
                  }
                }
              }
            }
          });
        }

        return updated;
      });
    },
    [localFormData]
  );

  // Helper: Find widget by field name
  const getWidgetByName = useCallback(
    (fieldName: string): any => {
      if (!localFormData) return null;

      if (localFormData.__root) {
        // Flat format
        return localFormData[fieldName];
      } else if (localFormData.rows) {
        // Hierarchical format
        for (const row of localFormData.rows) {
          for (const col of row.columns) {
            for (const widget of col.widgets) {
              if (widget.name === fieldName) return widget;
            }
          }
        }
      }
      return null;
    },
    [localFormData]
  );

  // Debounced field update
  const debouncedUpdate = useCallback(
    debounce((fieldName: string, value: any) => {
      // Build form_state from ALL field values (not just __state_fields)
      // This ensures the entire form state is preserved across stateless PATCH requests
      const formState = { ...allFieldValuesRef.current };

      // Use patch_url pattern from __form_meta if available
      const patchUrlPattern = formMetaRef.current?.patch_url;

      console.log('[RUL] PATCH (debounced)', {
        fieldName,
        value,
        formState,
        stateFields: stateFieldsRef.current,
        prefilledFields: prefilledFieldsRef.current,
      });

      updateFieldMutation.mutate(
        { fieldName, value, formState, patchUrlPattern },
        {
          onSuccess: (response) => {
            // Merge response with existing form data (backend returns only changed widgets)
            mergeFormResponse(response);

            // Update tracked field values from response
            for (const [key, widget] of Object.entries(response)) {
              if (
                !key.startsWith('__') &&
                widget &&
                typeof widget === 'object'
              ) {
                const widgetValue = getWidgetValue(widget as any);
                if (widgetValue !== null) {
                  allFieldValuesRef.current[key] = widgetValue;
                }
              }
            }

            // Handle __validation (new format)
            if (response.__validation) {
              setErrors(response.__validation.errors || {});
              setWarnings(response.__validation.warnings || {});
            } else {
              // Fallback to old format
              setErrors(response.validation_errors || {});
            }

            // Handle __auto_changed - update fields that backend automatically changed
            if (response.__auto_changed) {
              Object.entries(response.__auto_changed).forEach(
                ([field, newValue]) => {
                  allFieldValuesRef.current[field] = newValue;
                }
              );
            }

            // Handle __resets - clear reset fields in tracked values
            if (response.__resets) {
              response.__resets.forEach((field: string) => {
                allFieldValuesRef.current[field] = null;
                // Clear errors/warnings for reset fields
                setErrors((prev) => {
                  const next = { ...prev };
                  delete next[field];
                  return next;
                });
                setWarnings((prev) => {
                  const next = { ...prev };
                  delete next[field];
                  return next;
                });
              });
            }

            // Handle __refresh_options - log for debugging
            // NOTE: We don't call refreshDropdownOptions here because:
            // 1. Backend already sends updated widget with resolved url_params
            // 2. mergeFormResponse updates the widget in localFormData
            // 3. SimpleDropdownWidget's useEffect sees url_params change and refetches
            if (response.__refresh_options) {
              console.log(
                '[RUL] Fields to refresh (handled by mergeFormResponse):',
                response.__refresh_options
              );
            }
          },
          onError: (error) => {
            console.error('Field update error:', error);
          },
        }
      );
    }, debounceMs),
    [updateFieldMutation, debounceMs, mergeFormResponse]
  );

  // Immediate (non-debounced) field update for blur trigger
  const immediateUpdate = useCallback(
    (fieldName: string, value: any) => {
      const formState = { ...allFieldValuesRef.current };
      const patchUrlPattern = formMetaRef.current?.patch_url;

      console.log('[RUL] PATCH (immediate)', {
        fieldName,
        value,
        formState,
        stateFields: stateFieldsRef.current,
        prefilledFields: prefilledFieldsRef.current,
      });

      updateFieldMutation.mutate(
        { fieldName, value, formState, patchUrlPattern },
        {
          onSuccess: (response) => {
            // Merge response with existing form data (backend returns only changed widgets)
            mergeFormResponse(response);

            // Update tracked field values from response
            for (const [key, widget] of Object.entries(response)) {
              if (
                !key.startsWith('__') &&
                widget &&
                typeof widget === 'object'
              ) {
                const widgetValue = getWidgetValue(widget as any);
                if (widgetValue !== null) {
                  allFieldValuesRef.current[key] = widgetValue;
                }
              }
            }

            // Handle __validation
            if (response.__validation) {
              setErrors(response.__validation.errors || {});
              setWarnings(response.__validation.warnings || {});
            } else {
              setErrors(response.validation_errors || {});
            }

            // Handle __auto_changed
            if (response.__auto_changed) {
              Object.entries(response.__auto_changed).forEach(
                ([field, newValue]) => {
                  allFieldValuesRef.current[field] = newValue;
                }
              );
            }

            // Handle __resets - clear reset fields in tracked values
            if (response.__resets) {
              response.__resets.forEach((field: string) => {
                allFieldValuesRef.current[field] = null;
                setErrors((prev) => {
                  const next = { ...prev };
                  delete next[field];
                  return next;
                });
                setWarnings((prev) => {
                  const next = { ...prev };
                  delete next[field];
                  return next;
                });
              });
            }

            // Handle __refresh_options - log for debugging
            // NOTE: Backend sends updated widget with resolved url_params,
            // mergeFormResponse handles the update, dropdown auto-refetches
            if (response.__refresh_options) {
              console.log(
                '[RUL] Fields to refresh (handled by mergeFormResponse):',
                response.__refresh_options
              );
            }
          },
          onError: (error) => {
            console.error('Field update error:', error);
          },
        }
      );
    },
    [updateFieldMutation, mergeFormResponse]
  );

  // Handle field change - updates UI and triggers PATCH based on patch_trigger
  const handleFieldChange = (fieldName: string, value: any) => {
    // Update field value immediately in our tracking ref
    allFieldValuesRef.current[fieldName] = value;

    // Optimistic update for responsive UI
    if (localFormData) {
      const updatedForm = JSON.parse(JSON.stringify(localFormData));

      if (isHierarchical) {
        updatedForm.rows.forEach((row: any) => {
          row.columns.forEach((col: any) => {
            col.widgets.forEach((widget: any) => {
              if (widget.name === fieldName) {
                widget.value = value;
              }
            });
          });
        });
      } else if (isFlat) {
        if (updatedForm[fieldName]) {
          updatedForm[fieldName].value = value;
        }
      }

      setLocalFormData(updatedForm);
    }

    // Check patch_trigger - only send PATCH on change/keyup, not blur
    const widget = getWidgetByName(fieldName);
    const trigger = getPatchTrigger(widget);

    console.log('[RUL] handleFieldChange', {
      fieldName,
      value,
      trigger,
      hasPatchTrigger: widget?.patch_trigger,
    });

    if (trigger === 'change' || trigger === 'keyup') {
      // Frontend validation before PATCH
      if (!validateFrontend(widget, value)) {
        return; // Don't send PATCH if frontend validation fails
      }
      debouncedUpdate(fieldName, value);
    }
    // For 'blur' trigger, PATCH is sent in handleFieldBlur
  };

  // Handle field blur - sends PATCH immediately for blur trigger
  const handleFieldBlur = (fieldName: string, _value: any) => {
    const widget = getWidgetByName(fieldName);
    const trigger = getPatchTrigger(widget);

    console.log('[RUL] handleFieldBlur', {
      fieldName,
      trigger,
      widget: widget?.type,
    });

    if (trigger === 'blur') {
      // Use value from allFieldValuesRef which is always up to date
      // (the passed _value might be stale for some components like InputNumber)
      const currentValue = allFieldValuesRef.current[fieldName];
      console.log(
        '[RUL] Sending blur PATCH for',
        fieldName,
        'value:',
        currentValue
      );

      // Frontend validation before PATCH
      if (!validateFrontend(widget, currentValue)) {
        return; // Don't send PATCH if frontend validation fails
      }
      immediateUpdate(fieldName, currentValue);
    }
  };

  // Handle form submission
  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    if (!localFormData) return;

    // Use ALL field values from our tracking ref (includes prefilled fields)
    // This ensures we send complete form state to stateless backend
    const values: Record<string, any> = { ...allFieldValuesRef.current };

    // Filter out non-value widget types if present
    const nonValueTypes = [
      'label',
      'divider',
      'button',
      'logo',
      'link',
      'flex',
    ];

    // Check visibility for flat format fields
    if (isFlat && localFormData.__root) {
      localFormData.__root.forEach((fieldName: string) => {
        const widget = localFormData[fieldName];
        if (widget) {
          // Remove non-value types
          if (nonValueTypes.includes(widget.type)) {
            delete values[fieldName];
          }
          // Remove hidden fields
          if (!evaluateExpression(widget.visible_when, localFormData)) {
            delete values[fieldName];
          }
        }
      });
    }

    console.log('[RUL] handleSubmit values:', values);

    // Extract submit URL and method from __form_meta (supports edit mode with PUT)
    const submitButton = localFormData.submit_button;
    const submitUrl =
      formMetaRef.current?.submit_url || submitButton?.action?.url;
    const submitMethod =
      formMetaRef.current?.submit_method ||
      submitButton?.action?.method ||
      'POST';

    // Clear previous submit error
    setSubmitError(null);

    submitMutation.mutate(
      { data: values, submitUrl, method: submitMethod },
      {
        onSuccess: (result) => {
          if (result.success) {
            setErrors({});
            setSubmitError(null);
            if (onSuccess) {
              onSuccess(result.data);
            }
          } else {
            const validationErrors = result.validation_errors || {};
            setErrors(validationErrors);
            if (onError) {
              onError(validationErrors);
            }
          }
        },
        onError: (error: any) => {
          console.error('Form submission error:', error);

          // Parse error detail from backend
          const detail = error?.response?.data?.detail;
          let errorMessage = 'Failed to submit form';

          if (detail) {
            // Check if it's a resource conflict error (array in string)
            if (
              typeof detail === 'string' &&
              detail.includes('Resource conflicts detected')
            ) {
              try {
                // Extract the array part from the string
                const arrayMatch = detail.match(/\[.*\]/s);
                if (arrayMatch) {
                  // Parse the Python-style dict to JSON
                  const jsonStr = arrayMatch[0]
                    .replace(/'/g, '"')
                    .replace(/None/g, 'null')
                    .replace(/True/g, 'true')
                    .replace(/False/g, 'false');
                  const conflicts = JSON.parse(jsonStr);

                  // Format conflict messages
                  const conflictMessages = conflicts.map((c: any) => {
                    const from = new Date(c.occupied_from).toLocaleTimeString(
                      'sr-RS',
                      {
                        hour: '2-digit',
                        minute: '2-digit',
                      }
                    );
                    const until = new Date(c.occupied_until).toLocaleTimeString(
                      'sr-RS',
                      {
                        hour: '2-digit',
                        minute: '2-digit',
                      }
                    );
                    return `${c.resource_name}: ${c.event_title} (${from} - ${until})`;
                  });

                  errorMessage = `Resource conflict:\n${conflictMessages.join('\n')}`;
                }
              } catch (parseError) {
                console.error('Failed to parse conflict details:', parseError);
                errorMessage = detail;
              }
            } else if (typeof detail === 'string') {
              errorMessage = detail;
            } else if (detail.validation_errors) {
              // Handle validation errors in detail
              setErrors(detail.validation_errors);
              if (onError) {
                onError(detail.validation_errors);
              }
              return;
            }
          }

          setSubmitError(errorMessage);
          if (onError) {
            onError({ _error: errorMessage });
          }
        },
      }
    );
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="rul-form-loading">
        <Spin size="large" />
      </div>
    );
  }

  // Error state
  if (loadError) {
    return (
      <Alert
        message="Failed to load form"
        description={loadError.message}
        type="error"
        showIcon
      />
    );
  }

  // No data
  if (!localFormData) {
    return (
      <Alert
        message="No form data"
        description="The form could not be loaded."
        type="warning"
        showIcon
      />
    );
  }

  // Render hierarchical format (rows/columns/widgets)
  if (isHierarchical) {
    return (
      <form
        onSubmit={handleSubmit}
        className={`rul-form universal-rul-form ${className}`}
      >
        {submitError && (
          <div ref={errorAlertRef}>
            <Alert
              message="Submission Error"
              description={submitError}
              type="error"
              showIcon
              closable
              onClose={() => setSubmitError(null)}
              style={{ marginBottom: 16, whiteSpace: 'pre-line' }}
            />
          </div>
        )}
        {localFormData.rows.map((row: any, rowIndex: number) => (
          <Row key={rowIndex} gutter={16} className="rul-form-row">
            {row.columns.map((column: any, colIndex: number) => {
              const span = column.width
                ? Math.round((column.width / 12) * 24)
                : 24;
              return (
                <Col key={colIndex} span={span} className="rul-form-column">
                  {column.widgets.map((widget: any, widgetIndex: number) => (
                    <div key={widgetIndex}>
                      {renderWidget(
                        widget,
                        handleFieldChange,
                        errors[widget.name],
                        warnings[widget.name],
                        widget.type === 'button' &&
                          widget.button_type === 'submit'
                          ? handleSubmit
                          : undefined,
                        undefined,
                        handleFieldBlur
                      )}
                    </div>
                  ))}
                </Col>
              );
            })}
          </Row>
        ))}
        {showSubmitButton && !hasSubmitButton(localFormData) && (
          <div className="rul-form-actions">
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={submitMutation.isPending}
              block
            >
              {formMetaRef.current?.submit_label || 'Submit'}
            </Button>
          </div>
        )}
      </form>
    );
  }

  // Render flat format (__root array)
  if (isFlat) {
    return (
      <form
        onSubmit={handleSubmit}
        className={`rul-form universal-rul-form flat-format ${className}`}
      >
        {submitError && (
          <div ref={errorAlertRef}>
            <Alert
              message="Submission Error"
              description={submitError}
              type="error"
              showIcon
              closable
              onClose={() => setSubmitError(null)}
              style={{ marginBottom: 16, whiteSpace: 'pre-line' }}
            />
          </div>
        )}
        {localFormData.__root.map((fieldName: string) => {
          const widget = localFormData[fieldName];
          if (!widget) return null;

          // Check visible_when condition
          const isVisible = evaluateExpression(
            widget.visible_when,
            localFormData
          );
          if (!isVisible) return null;

          // Check enabled_when condition
          const isEnabled =
            widget.enabled !== false &&
            evaluateExpression(widget.enabled_when, localFormData);

          // Add name and computed enabled state to widget for handlers
          const widgetWithName = {
            ...widget,
            name: fieldName,
            enabled: isEnabled,
          };

          return (
            <div key={fieldName}>
              {renderWidget(
                widgetWithName,
                handleFieldChange,
                errors[fieldName],
                warnings[fieldName],
                undefined,
                {
                  formData: localFormData,
                  allErrors: errors,
                  allWarnings: warnings,
                },
                handleFieldBlur
              )}
            </div>
          );
        })}
        {showSubmitButton && (
          <div className="rul-form-actions">
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={submitMutation.isPending}
              block
            >
              {formMetaRef.current?.submit_label || 'Submit'}
            </Button>
          </div>
        )}
      </form>
    );
  }

  // Unknown format
  return (
    <Alert
      message="Unknown form format"
      description="Form data must contain either 'rows' (hierarchical) or '__root' (flat) property."
      type="error"
      showIcon
    />
  );
};

/**
 * Helper: Check if form already has a submit button widget
 */
function hasSubmitButton(formData: any): boolean {
  if (formData.rows) {
    return formData.rows.some((row: any) =>
      row.columns.some((col: any) =>
        col.widgets.some(
          (widget: any) =>
            widget.type === 'button' && widget.button_type === 'submit'
        )
      )
    );
  }
  return false;
}
